/**
 * Bottom-dock Jobs list. Live runs come from the shell store; history from
 * list_jobs.
 *
 * Plural: a batch on a machine with the memory for it has several runs in
 * flight, and clicking one follows it — the row is how a reader gets from "two
 * things are going" to "show me that one".
 */

import { useEffect, useState } from "react";
import { EmptyState } from "@/components/ui/primitives";
import { listJobs } from "@/ipc/api";
import type { JobStatus } from "@/ipc/contract";
import { formatDateTime, formatDuration } from "@/lib/utils";
import { goToTab } from "@/pipeline/actions";
import { JOB_LABEL, JobGlyph } from "@/pipeline/status";
import { cn } from "@/lib/utils";
import { useAppStore, useJob, useRunningJobs } from "@/store/appStore";

export function JobsPanel() {
  const live = useJob();
  const running = useRunningJobs();
  const focusJob = useAppStore((s) => s.focusJob);
  const [history, setHistory] = useState<JobStatus[]>([]);

  useEffect(() => {
    let cancelled = false;
    void listJobs(undefined, 12)
      .then((rows) => {
        if (!cancelled) setHistory(rows);
      })
      .catch(() => {
        if (!cancelled) setHistory([]);
      });
    return () => {
      cancelled = true;
    };
  }, [live?.jobId, live?.state]);

  const rows = mergeJobs(live, running, history);

  if (rows.length === 0) {
    return <EmptyState title="No jobs" hint="Run a pipeline from the Pipeline tab." />;
  }

  return (
    <div className="h-full overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-neutral-200 text-left text-[11px] text-neutral-500">
            <th className="px-3 py-1.5 font-semibold">State</th>
            <th className="px-3 py-1.5 font-semibold">Subject</th>
            <th className="px-3 py-1.5 font-semibold">Stage</th>
            <th className="px-3 py-1.5 font-semibold">Started</th>
            <th className="px-3 py-1.5 font-semibold">Duration</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((job) => {
            const duration =
              job.startedIso && job.finishedIso
                ? (new Date(job.finishedIso).getTime() - new Date(job.startedIso).getTime()) / 1000
                : null;
            return (
              <tr
                key={job.jobId}
                className={cn(
                  "cursor-pointer border-b border-neutral-100 hover:bg-neutral-50",
                  job.jobId === live?.jobId && "bg-ads-accent-soft",
                )}
                title={job.jobId === live?.jobId ? "The run the workspace is following" : "Follow this run"}
                onClick={() => {
                  focusJob(job.jobId);
                  goToTab("pipeline");
                }}
              >
                <td className="px-3 py-1.5">
                  <span className="inline-flex items-center gap-1.5">
                    <JobGlyph state={job.state} />
                    {JOB_LABEL[job.state]}
                    {job.state === "running" ? ` ${job.progressPct}%` : ""}
                  </span>
                </td>
                <td className="px-3 py-1.5 font-medium">
                  {job.subjectId || job.caseId.slice(0, 8)}
                </td>
                <td className="px-3 py-1.5">
                  {job.currentModality && job.currentStage
                    ? `${job.currentModality} · ${job.currentStage}`
                    : "—"}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap">{formatDateTime(job.startedIso)}</td>
                <td className="px-3 py-1.5">{formatDuration(duration)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The run in focus first, then anything else still going, then history. A run
 * this session started is preferred over the backend's copy of it, because the
 * store has the stage that arrived a moment ago and the fetched list does not.
 */
function mergeJobs(
  live: JobStatus | null,
  running: JobStatus[],
  history: JobStatus[],
): JobStatus[] {
  const order = [
    ...(live ? [live] : []),
    ...running.filter((j) => j.jobId !== live?.jobId),
  ];
  const seen = new Set(order.map((j) => j.jobId));
  return [...order, ...history.filter((j) => !seen.has(j.jobId))];
}
